import React from 'react';
import { WIDGETS } from '../../config/widgets';

/**
 * Simple error boundary for widgets
 */
class WidgetErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          background: '#FEE2E2',
          borderRadius: '8px',
          color: '#DC2626',
          fontSize: '13px',
        }}>
          <strong>Widget Error:</strong> {this.props.widgetId}
          <br />
          <small>{this.state.error?.message}</small>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * WidgetGrid - Auto-flow grid that renders widgets for a domain
 * 
 * Responsive:
 * - Mobile (<600px): 1 column
 * - Tablet (600-1024px): 2 columns
 * - Desktop (>1024px): auto-fit with min 280px
 */
export default function WidgetGrid({
  widgetIds,
  domain,
  widgetComponents,
  widgetProps = {},
}) {
  // Filter to only valid widgets
  const validWidgets = widgetIds
    .map(id => WIDGETS[id])
    .filter(widget => widget && widget.availableIn.includes(domain));

  return (
    <div 
      className="widget-grid-container"
      style={{
        display: 'grid',
        gap: '12px',
        width: '100%',
      }}
    >
      <style>{`
        .widget-grid-container {
          grid-template-columns: repeat(2, 1fr);
        }
        @media (max-width: 500px) {
          .widget-grid-container {
            grid-template-columns: 1fr;
          }
          .widget-grid-item {
            grid-column: span 1 !important;
          }
        }
      `}</style>
      
      {validWidgets.map(widget => {
        // Look up component by widget ID
        const Component = widgetComponents[widget.id];
        
        if (!Component) {
          console.warn(`Widget component not found for: ${widget.id}`);
          return (
            <div
              key={widget.id}
              style={{
                gridColumn: widget.size === 'full' ? 'span 2' : 'span 1',
                padding: '20px',
                background: '#FEF3C7',
                borderRadius: '8px',
                color: '#92400E',
                fontSize: '13px',
              }}
            >
              <strong>Missing Component:</strong> {widget.id}
              <br />
              <small>Check WIDGET_COMPONENTS in index.js</small>
            </div>
          );
        }

        // Determine grid span based on widget size
        const gridColumn = widget.size === 'full' ? 'span 2' : 'span 1';
        
        // Get props for this specific widget
        const props = widgetProps[widget.id] || {};

        return (
          <div
            key={widget.id}
            className="widget-grid-item"
            style={{
              gridColumn,
            }}
          >
            <WidgetErrorBoundary widgetId={widget.id}>
              <Component {...props} />
            </WidgetErrorBoundary>
          </div>
        );
      })}
    </div>
  );
}
